import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';

/**
 * LA CHINCHETA DE ESTA PUERTA, A MANO.
 *
 * El buscador de direcciones acierta con una calle y un número. No acierta con
 * «Urbanización Son Vida, chalet sin número», que es media Mallorca: deja la
 * chincheta en la entrada de la urbanización y la casa está a seiscientos
 * metros. Y el GPS del captador tampoco, porque estaba en la acera de enfrente.
 *
 * Esto es el último recurso y el único que nunca falla: se ve el mapa, se
 * arrastra la chincheta encima del tejado y ya está. Se toca el mapa y la
 * chincheta va allí, que en un móvil es más fácil que arrastrar.
 *
 * NO GUARDA NADA por su cuenta: mueve el punto en el borrador y quien lo mira
 * decide si guarda. Un mapa que guarda al rozarlo es un mapa que da miedo.
 */

interface Props {
  idioma: Idioma;
  lat: number;
  lon: number;
  onMover: (lat: number, lon: number) => void;
}

/** Mallorca entera, para cuando la puerta no tiene punto todavía. */
const ISLA: L.LatLngBoundsExpression = [
  [39.26, 2.33],
  [39.96, 3.46],
];

const CHINCHETA = `<svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
  <path d="M15 37C15 37 28 22.5 28 14A13 13 0 1 0 2 14c0 8.5 13 23 13 23z"
        fill="#8a6d3b" stroke="#fff" stroke-width="2" />
  <circle cx="15" cy="14" r="5" fill="#fff" />
</svg>`;

export function SituarPuerta({ idioma, lat, lon, onMover }: Props) {
  const caja = useRef<HTMLDivElement>(null);
  const mapa = useRef<L.Map | null>(null);
  const marca = useRef<L.Marker | null>(null);
  const mover = useRef(onMover);
  mover.current = onMover;
  const [config, setConfig] = useState<Awaited<ReturnType<typeof api.mapaConfig>> | null>(null);

  useEffect(() => {
    api.mapaConfig().then(setConfig).catch(() => undefined);
  }, []);

  const tienePunto = Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);

  useEffect(() => {
    if (!caja.current || !config || mapa.current) return;
    const m = L.map(caja.current, { zoomControl: true, scrollWheelZoom: false });
    if (tienePunto) m.setView([lat, lon], 18);
    else m.fitBounds(ISLA);

    L.tileLayer(config.calle.url, {
      attribution: config.calle.atribucion,
      maxZoom: config.calle.zoomMaximo,
    }).addTo(m);

    const icono = L.divIcon({
      html: CHINCHETA,
      className: 'chincheta-situar',
      iconSize: [30, 38],
      iconAnchor: [15, 38],
    });
    const punto = L.marker(tienePunto ? [lat, lon] : m.getCenter(), {
      icon: icono,
      draggable: true,
      keyboard: true,
    }).addTo(m);
    punto.on('dragend', () => {
      const p = punto.getLatLng();
      mover.current(p.lat, p.lng);
    });
    // Tocar el mapa la trae: en un móvil es más fácil que arrastrar.
    m.on('click', (e: L.LeafletMouseEvent) => {
      punto.setLatLng(e.latlng);
      mover.current(e.latlng.lat, e.latlng.lng);
    });
    marca.current = punto;
    mapa.current = m;

    const observador = new ResizeObserver(() => m.invalidateSize());
    observador.observe(caja.current);

    return () => {
      observador.disconnect();
      m.remove();
      mapa.current = null;
      marca.current = null;
    };
    // El mapa se monta una vez; mover la chincheta después no lo rehace.
  }, [config]);

  /*
    Cuando el buscador encuentra la dirección, el punto cambia desde fuera: la
    chincheta se va allí y el mapa la sigue. Sin esto se pulsaría «buscar» y el
    mapa seguiría enseñando el sitio viejo.
  */
  useEffect(() => {
    if (!mapa.current || !marca.current || !tienePunto) return;
    const actual = marca.current.getLatLng();
    if (Math.abs(actual.lat - lat) < 1e-9 && Math.abs(actual.lng - lon) < 1e-9) return;
    marca.current.setLatLng([lat, lon]);
    mapa.current.setView([lat, lon], Math.max(mapa.current.getZoom(), 17));
  }, [lat, lon, tienePunto]);

  return (
    <div className="campo">
      <span className="etiqueta-campo">{t(idioma, 'pap.situarTitulo')}</span>
      <div ref={caja} className="pap-mapa-situar" role="application" aria-label={t(idioma, 'pap.situarTitulo')} />
      <small className="suave">{t(idioma, 'pap.situarPie')}</small>
    </div>
  );
}
